import React, { ReactNode, useContext, useMemo } from 'react';
import { Layout, Row, Col, Select, Checkbox, Button, DatePicker, Typography, Space } from 'antd';
import styles from '../Style/LayoutPanel.module.scss'
import IconPoint from '../../../../assets/icon/locate2.png'
import { IBestDataset, IItemsValidation, IPlotVariable } from '../../../../types/analytics.d';
import moment, { Moment } from 'moment';
import CustomDivider from './CustomDIvider';
import { StoreContext } from '../../../../store/store';
import {useMarkerStore} from "../../../../store/markerStore";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

type VariableType = IPlotVariable[] | IItemsValidation[];

interface LayoutPanelProps {
  fieldColumnFields?: ReactNode[];
  mainContent: ReactNode;
  extraColumnFields?: ReactNode[];
  datasetOptions: IBestDataset[];
  variableOptions: VariableType;
  onPointChange: (value: string) => void;
  onDatasetChange: (value: string) => void;
  onVariableChange: (value: number | string) => void;
  onUpdate: () => void;
  onStartDateChange: (value: any) => void;
  onEndDateChange: (value: any) => void;
  showCheckbox: boolean;
  onShowCheckboxChange?: (checked: boolean) => void;
  point?: string;
  dataset?: string;
  variable?: string | number;
  startDate?: Moment;
  endDate?: Moment;
  checkboxChecked?: boolean;
  disableStartDate: (val: Moment) => boolean
  disableEndDate: (val: Moment) => boolean
  disabledDataset?: boolean
  disabledVariable?: boolean
  disabledUpdateButton? : boolean
  chartDefinition?: string | ReactNode
}

const LayoutPanel: React.FC<LayoutPanelProps> = ({
  fieldColumnFields = [],
  mainContent,
  extraColumnFields = [],
  datasetOptions,
  variableOptions,
  showCheckbox,
  chartDefinition,
  onPointChange,
  onDatasetChange,
  onVariableChange,
  onShowCheckboxChange,
  onUpdate,
  onStartDateChange,
  onEndDateChange,
  disableEndDate,
  disableStartDate,
  point,
  dataset,
  variable,
  startDate,
  endDate,
  disabledDataset = false,
  disabledUpdateButton = false,
  disabledVariable = false,
  checkboxChecked = false,
}) => {

  const markers = useMarkerStore(state => state.markers)

  const getOptionLabel = (item: IPlotVariable | IItemsValidation): string => {
    if ('Name' in item) {
      return `${item.Name}${item.Unit ? ` (${item.Unit})` : ''}`;
    } else {
      return `${item.details}${item.unit ? ` (${item.unit})` : ''}`;
    }
  };

  const getOptionValue = (item: IPlotVariable | IItemsValidation): string | number => {
    if ('Index' in item) {
      return item.Index;
    } else {
      return item.name;
    }
  };

  const categories = useMemo(() => {
    const labels = datasetOptions.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(labels));
  }, [datasetOptions]);


  const pointOptions = useMemo(() => {
    const points = markers?.map((item) => ({label : item.label, value: item.id}))
    return points ?? []
  }, [markers])

  return (
    <Layout style={{minWidth: 1200, height: '100%', overflow: 'hidden', background: 'white'}}>
      <Header className={styles.header}>
         <Row justify="space-between" align="middle" className={styles.headerRow}>
          <Col>
            <Row gutter={[0, 8]} align="bottom">
              <Col className={styles.colPoint}>
              <Space>
                <Text>Point</Text>
                <img src={IconPoint} width={20} height={20} />
              </Space>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select point"
                onChange={onPointChange}
                options={pointOptions}
                value={point}
              />
              </Col>
              <Col span={24} md={!showCheckbox ? 6 : 4} className={styles.colItem}>
                <Text>Dataset</Text>
                <br />
                <Select
                  value={dataset}
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  onChange={onDatasetChange}
                  placeholder="Select dataset"
                  disabled={disabledDataset}
                >
                  {categories.map((category: string) => (
                    <Select.OptGroup key={category} label={category}>
                      {datasetOptions
                        .filter((item) => item.label === category)
                        .map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.value}
                          </Select.Option>
                        ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              </Col>
              <Col span={24} md={!showCheckbox ? 6 : 4} className={styles.colItem}>
                <Text>Variable</Text>
                <br />
                <Select
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  placeholder="Select variable"
                  onChange={onVariableChange}
                  options={variableOptions.map(item => ({
                    value: getOptionValue(item),
                    label: getOptionLabel(item)
                  }))}
                  value={variable}
                  disabled={disabledVariable}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>Start Date</Text>
                <DatePicker
                  onChange={onStartDateChange}
                  style={{ width: '100%', marginTop: '4px' }}
                  placeholder="Select start date"
                  disabledDate={disableStartDate}
                  value={startDate ? moment(startDate) : null}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>End Date</Text>
                <DatePicker
                  onChange={onEndDateChange}
                  disabledDate={disableEndDate}
                  style={{ width: '100%', marginTop: '4px' }}
                  placeholder="Select end date"
                  value={endDate ? moment(endDate) : null}
                />
              </Col>
              {showCheckbox && (
                <Col span={24} md={3} style={{marginLeft: 20}} className={styles.colItem}>
                  <div style={{display: 'flex', width: 180, alignItems: 'center'}}>
                    <Text>Use this period for all analytics</Text>
                    <Checkbox
                      onChange={(e) => onShowCheckboxChange?.(e.target.checked)}
                      checked={checkboxChecked}
                      style={{
                        transform: 'scale(1.5)',
                        marginRight: '12px'
                      }}
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Col>
          <Col style={{width: 201}}>
          <div style={{height: '60px', padding: 20, borderLeft: '2px solid #f0f0f0'}}>
            <Button type="primary" disabled={disabledUpdateButton} onClick={onUpdate} style={{ width: 100, bottom: 10}}>
              Update
            </Button>
          </div>
          </Col>
        </Row>
      </Header>
      <CustomDivider  />
      <Layout>
        <Sider theme="light">
          {fieldColumnFields.map((field, index) => (
            <div key={index} style={{ marginTop: '20px' }}>
              {field}
            </div>
          ))}
        </Sider>
        <Content style={{ padding: '10px', minHeight: '400px', borderLeft: '2px solid #f0f0f0', borderRight: '2px solid #f0f0f0', background: 'white'}}>
          {mainContent}
        </Content>
        <Sider
          width={200}
          className={styles.siderChildren}
          theme="light"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 500, overflowX: 'hidden', }}>
            {extraColumnFields.map((field, index) => (
              <div key={index}>{field}</div>
            ))}
          </div>
          <div style={{
            textAlign: 'center',
            marginTop: 'auto'
          }}>
            {chartDefinition}
          </div>
        </Sider>
      </Layout>
    </Layout>
  );
};

export default LayoutPanel;
